import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Image,
  Input,
  Select,
  SimpleGrid,
  Switch,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Autocomplete } from '@react-google-maps/api';
import { FaCamera, FaMapMarkerAlt, FaMedal, FaTrash } from 'react-icons/fa';
import { updateEmail } from 'firebase/auth';
import { addDoc, arrayUnion, collection, doc, getFirestore, onSnapshot, query, updateDoc, where } from 'firebase/firestore';
import { getDownloadURL, getStorage, ref, uploadBytesResumable } from 'firebase/storage';
import Card from 'components/card/Card';
import GoogleMapsLoader from 'components/map/GoogleMapsLoader';
import { AuthContext } from 'contexts/AuthContext';
import auth, { app } from 'lib/firebase';
import { CLASS_TYPE_ICONS, getClassTypeIcon } from 'utils/classIcons';
import { buildAssignedInstructorClassesQuery } from 'utils/instructorClassQueries';
import {
  TRAVEL_DISTANCE_OPTIONS,
  getInstructorProfileBadges,
  normalizeInstructorClassTypes,
  normalizeInstructorCustomClassTypes,
} from 'utils/instructorProfile';
import {
  buildInstructorCommunicationPreferenceActivity,
  canInstructorManageCommunicationPreference,
  getInstructorCommunicationBadge,
  getNextCommunicationAccountStatus,
  isInstructorOptedIntoCommunication,
} from 'utils/instructorCommunicationPreferences';
import { ACCOUNT_STATUS_STRIKE_PAUSED } from 'utils/lateCancellationStrikes';
import { buildStrikeAppealEmailPayload } from 'utils/lateCancellationStrikeNotifications';

const db = getFirestore(app);
const storage = getStorage(app);
const US_ADDRESS_AUTOCOMPLETE_OPTIONS = {
  componentRestrictions: { country: 'us' },
  fields: ['address_components', 'formatted_address', 'geometry'],
  types: ['address'],
};
const STATE_COMPONENT_TYPES = ['administrative_area_level_1'];
const CITY_COMPONENT_TYPES = ['locality', 'postal_town', 'sublocality'];

const getAddressComponent = (place, types, field = 'long_name') => {
  const component = place?.address_components?.find((item) => (
    types.some((type) => item.types.includes(type))
  ));

  return component?.[field] || '';
};

const isCompletedClass = (classData) => {
  const status = classData?.status;
  const classStatus = classData?.classStatus;

  return classData?.DidTeachClass === true
    || (status !== undefined && status !== null && String(status).trim() !== '')
    || String(classStatus) === '6';
};

export default function InstructorProfile() {
  const { userDocId, userData, refreshUserData } = useContext(AuthContext);
  const fileInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const cancelPauseRef = useRef(null);
  const [form, setForm] = useState({
    FirstName: '',
    LastName: '',
    Email: '',
    Phone: '',
    Address: '',
    City: '',
    State: '',
    Zip: '',
    location: null,
    distanceValue: '',
    ClassTypes: [],
    CustomClassTypes: [],
    ProfileImageURL: '',
  });
  const [assignedClasses, setAssignedClasses] = useState([]);
  const [selectedClassType, setSelectedClassType] = useState('');
  const [customClassTypeInput, setCustomClassTypeInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isUpdatingCommunicationPreference, setIsUpdatingCommunicationPreference] = useState(false);
  const [strikeAppealMessage, setStrikeAppealMessage] = useState('');
  const [isSubmittingStrikeAppeal, setIsSubmittingStrikeAppeal] = useState(false);
  const pauseOutreachDisclosure = useDisclosure();
  const toast = useToast();

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const softBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const selectedTravelBg = useColorModeValue('blue.50', 'whiteAlpha.200');
  const selectedTravelBorder = useColorModeValue('blue.400', 'blue.300');
  const inputBg = useColorModeValue('white', 'whiteAlpha.100');
  const inputTextColor = useColorModeValue('secondaryGray.900', 'white');
  const inputPlaceholderColor = useColorModeValue('gray.500', 'gray.300');
  const disabledInputBg = useColorModeValue('gray.100', 'whiteAlpha.200');
  const disabledInputTextColor = useColorModeValue('gray.700', 'gray.100');
  const selectOptionBg = useColorModeValue('white', '#1A202C');
  const strikePausedBg = useColorModeValue('red.50', 'whiteAlpha.100');
  const profileInputStyles = {
    bg: inputBg,
    color: inputTextColor,
    borderColor,
    _placeholder: { color: inputPlaceholderColor },
    _hover: { borderColor: selectedTravelBorder },
    _focus: {
      borderColor: selectedTravelBorder,
      boxShadow: `0 0 0 1px ${selectedTravelBorder}`,
    },
    _disabled: {
      bg: disabledInputBg,
      color: disabledInputTextColor,
      cursor: 'not-allowed',
      opacity: 1,
    },
  };
  const profileSelectStyles = {
    ...profileInputStyles,
    sx: {
      '& option': {
        background: selectOptionBg,
        color: inputTextColor,
      },
    },
  };

  useEffect(() => {
    setForm({
      FirstName: userData?.FirstName || '',
      LastName: userData?.LastName || '',
      Email: userData?.Email || '',
      Phone: userData?.Phone || '',
      Address: userData?.Address || '',
      City: userData?.City || '',
      State: userData?.State || '',
      Zip: userData?.Zip || userData?.ZipCode || '',
      location: userData?.location || null,
      distanceValue: userData?.distanceValue || '',
      ClassTypes: normalizeInstructorClassTypes(userData?.ClassTypes),
      CustomClassTypes: normalizeInstructorCustomClassTypes(userData?.CustomClassTypes),
      ProfileImageURL: userData?.ProfileImageURL || '',
    });
  }, [userData]);

  useEffect(() => {
    if (!userDocId) return undefined;

    const classesQuery = buildAssignedInstructorClassesQuery({
      db,
      collection,
      query,
      where,
      instructorId: userDocId,
    });

    return onSnapshot(classesQuery, (snapshot) => {
      setAssignedClasses(snapshot.docs.map((classDoc) => ({
        id: classDoc.id,
        ...classDoc.data(),
      })));
    });
  }, [userDocId]);

  const assignedClassCount = assignedClasses.length;
  const completedClassCount = assignedClasses.filter(isCompletedClass).length;
  const profileBadges = useMemo(() => getInstructorProfileBadges({
    assignedClassCount,
    completedClassCount,
  }), [assignedClassCount, completedClassCount]);
  const availableClassTypes = useMemo(() => Object.keys(CLASS_TYPE_ICONS), []);
  const canManageCommunicationPreference = canInstructorManageCommunicationPreference(userData?.AccountStatus);
  const receivesOpportunityOutreach = canManageCommunicationPreference
    && isInstructorOptedIntoCommunication(userData?.AccountStatus);
  const communicationBadge = getInstructorCommunicationBadge(userData?.AccountStatus);
  const isStrikePaused = userData?.AccountStatus === ACCOUNT_STATUS_STRIKE_PAUSED;
  const isStrikeAppealSubmitted = Boolean(userData?.lateCancellationStrikeState?.appealSubmittedAt);
  const instructorDisplayName = `${form.FirstName || ''} ${form.LastName || ''}`.trim()
    || userData?.Email
    || form.Email
    || 'Instructor';

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    if (!place) return;

    const latitude = place.geometry?.location?.lat?.();
    const longitude = place.geometry?.location?.lng?.();

    setForm((current) => ({
      ...current,
      Address: place.formatted_address || current.Address,
      City: getAddressComponent(place, CITY_COMPONENT_TYPES) || current.City,
      State: getAddressComponent(place, STATE_COMPONENT_TYPES, 'short_name') || current.State,
      Zip: getAddressComponent(place, ['postal_code']) || current.Zip,
      location: Number.isFinite(latitude) && Number.isFinite(longitude)
        ? { latitude, longitude }
        : current.location,
    }));
  };

  const addClassType = () => {
    if (!selectedClassType) return;

    setForm((current) => {
      if (current.ClassTypes.includes(selectedClassType)) return current;

      return {
        ...current,
        ClassTypes: [...current.ClassTypes, selectedClassType],
      };
    });
    setSelectedClassType('');
  };

  const removeClassType = (classType) => {
    setForm((current) => ({
      ...current,
      ClassTypes: current.ClassTypes.filter((item) => item !== classType),
    }));
  };

  const addCustomClassType = () => {
    const classType = customClassTypeInput.trim().replace(/\s+/g, ' ');
    if (!classType) return;

    setForm((current) => {
      const customClassTypes = normalizeInstructorCustomClassTypes(current.CustomClassTypes);
      const existingClassTypes = [
        ...current.ClassTypes,
        ...customClassTypes.map((item) => item.value),
      ].map((item) => item.toLowerCase());

      if (existingClassTypes.includes(classType.toLowerCase())) return current;

      return {
        ...current,
        CustomClassTypes: [
          ...customClassTypes,
          {
            value: classType,
            source: 'user_entered',
            createdAt: Date.now(),
          },
        ],
      };
    });
    setCustomClassTypeInput('');
  };

  const removeCustomClassType = (classType) => {
    setForm((current) => ({
      ...current,
      CustomClassTypes: normalizeInstructorCustomClassTypes(current.CustomClassTypes)
        .filter((item) => item.value !== classType),
    }));
  };

  const saveProfilePhoto = async (ProfileImageURL) => {
    if (!userDocId) return;

    await updateDoc(doc(db, 'Users', userDocId), { ProfileImageURL });
    updateField('ProfileImageURL', ProfileImageURL);
    await refreshUserData();
  };

  const handleProfileImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !userDocId) return;

    setIsUploadingPhoto(true);
    try {
      const storagePath = `profile-images/${userDocId}/${Date.now()}-${file.name}`;
      const uploadTask = uploadBytesResumable(ref(storage, storagePath), file);
      await new Promise((resolve, reject) => {
        uploadTask.on('state_changed', undefined, reject, resolve);
      });
      const ProfileImageURL = await getDownloadURL(uploadTask.snapshot.ref);
      await saveProfilePhoto(ProfileImageURL);
      toast({
        title: 'Profile photo updated',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Unable to upload photo',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsUploadingPhoto(false);
      if (event.target) event.target.value = '';
    }
  };

  const handleRemoveProfileImage = async () => {
    setIsUploadingPhoto(true);
    try {
      await saveProfilePhoto('');
      toast({
        title: 'Profile photo removed',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Unable to remove photo',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleSave = async () => {
    if (!userDocId) return;

    setIsSaving(true);
    try {
      if (form.Email && form.Email !== userData?.Email) {
        if (!auth.currentUser) {
          throw new Error('You must be signed in again before changing your email.');
        }
        await updateEmail(auth.currentUser, form.Email);
      }

      await updateDoc(doc(db, 'Users', userDocId), {
        Phone: form.Phone,
        Email: form.Email,
        Address: form.Address,
        StreetAddress: form.Address,
        City: form.City,
        State: form.State,
        Zip: form.Zip,
        location: form.location,
        distanceValue: form.distanceValue,
        ClassTypes: form.ClassTypes,
        CustomClassTypes: normalizeInstructorCustomClassTypes(form.CustomClassTypes),
        ProfileImageURL: form.ProfileImageURL,
      });
      await refreshUserData();
      toast({
        title: 'Profile saved',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Unable to save profile',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const updateCommunicationPreference = async (nextStatus) => {
    if (!userDocId) return;

    setIsUpdatingCommunicationPreference(true);
    try {
      const nowMs = Date.now();
      await updateDoc(doc(db, 'Users', userDocId), {
        AccountStatus: nextStatus,
      });
      await addDoc(
        collection(db, 'Users', userDocId, 'Activity'),
        buildInstructorCommunicationPreferenceActivity({
          userDocId,
          email: userData?.Email || form.Email,
          nextStatus,
          nowMs,
        }),
      );
      await refreshUserData();
      toast({
        title: nextStatus === 'Paused' ? 'Class opportunity outreach paused' : 'Class opportunity outreach resumed',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Unable to update outreach preference',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsUpdatingCommunicationPreference(false);
    }
  };

  const handleCommunicationPreferenceChange = (event) => {
    if (!canManageCommunicationPreference) return;

    if (!event.target.checked) {
      pauseOutreachDisclosure.onOpen();
      return;
    }

    updateCommunicationPreference(getNextCommunicationAccountStatus(true));
  };

  const confirmPauseCommunicationPreference = async () => {
    pauseOutreachDisclosure.onClose();
    await updateCommunicationPreference(getNextCommunicationAccountStatus(false));
  };

  const handleSubmitStrikeAppeal = async () => {
    const message = strikeAppealMessage.trim();
    if (isStrikeAppealSubmitted) {
      toast({
        title: 'A review request has already been submitted.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!userDocId || !message) {
      toast({
        title: 'Add a message first',
        description: 'Tell us what happened so the team can review your account.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSubmittingStrikeAppeal(true);
    try {
      const nowMs = Date.now();
      const instructorRef = doc(db, 'Users', userDocId);
      await addDoc(collection(db, 'mail'), buildStrikeAppealEmailPayload({
        instructor: {
          id: userDocId,
          ...userData,
          Phone: userData?.Phone || form.Phone,
          Email: userData?.Email || form.Email,
        },
        message,
        nowMs,
      }));
      await addDoc(collection(db, 'Users', userDocId, 'Activity'), {
        from: 'Instructor Portal',
        to: 'Admin Team',
        type: 'note',
        title: 'Strike Appeal Submitted',
        body: 'Instructor submitted a strike appeal from the instructor portal.',
        timestamp: nowMs,
        user: userData?.Email || form.Email || '',
        actor: {
          userId: userDocId,
          email: userData?.Email || form.Email || null,
          name: instructorDisplayName,
          type: 'instructor',
        },
        metadata: {
          source: 'instructor_portal',
          appealMessage: message,
        },
      });
      await updateDoc(instructorRef, {
        lateCancellationStrikeState: {
          ...(userData?.lateCancellationStrikeState || {}),
          appealSubmittedAt: nowMs,
          appealSubmittedMessage: message,
          appealSubmittedSource: 'instructor_portal',
          appealStatus: 'submitted',
          updatedAt: nowMs,
        },
        adminNotes: arrayUnion({
          title: 'Strike Appeal Submitted',
          body: `Instructor submitted a strike appeal.\n\n${message}`,
          date: nowMs,
          user: `${instructorDisplayName} via Instructor Portal`,
        }),
      });
      await refreshUserData?.();
      setStrikeAppealMessage('');
      toast({
        title: 'Review request sent',
        description: 'The Fit Group USA team will review your message and follow up.',
        status: 'success',
        duration: 3500,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Unable to send review request',
        description: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingStrikeAppeal(false);
    }
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="instructor-profile-page">
      <SimpleGrid id="profile-details-widget" columns={{ base: 1, xl: 3 }} gap="20px">
        <Card p="24px">
          <Flex direction="column" align="center" gap="18px">
            <Avatar
              size="2xl"
              name={`${form.FirstName} ${form.LastName}`.trim()}
              src={form.ProfileImageURL}
            />
            <Box textAlign="center">
              <Text color={textColor} fontSize="xl" fontWeight="800">
                {`${form.FirstName} ${form.LastName}`.trim() || 'Instructor'}
              </Text>
              <Text color={mutedText} fontSize="sm" mt="4px">
                {form.Email || 'Email not set'}
              </Text>
              <Badge colorScheme={communicationBadge.colorScheme} mt="10px">
                {communicationBadge.label}
              </Badge>
            </Box>

            <VStack spacing="10px" w="100%">
              <Text color={textColor} fontSize="sm" fontWeight="700">
                Profile Photo
              </Text>
              <Input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                display="none"
                onChange={handleProfileImageUpload}
              />
              <HStack spacing="10px" justify="center" flexWrap="wrap">
                <Button
                  leftIcon={<Icon as={FaCamera} />}
                  colorScheme="brand"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  isLoading={isUploadingPhoto}
                >
                  Upload Photo
                </Button>
                <Button
                  leftIcon={<Icon as={FaTrash} />}
                  variant="outline"
                  colorScheme="red"
                  size="sm"
                  onClick={handleRemoveProfileImage}
                  isDisabled={!form.ProfileImageURL || isUploadingPhoto}
                >
                  Remove Photo
                </Button>
              </HStack>
            </VStack>

            <VStack align="stretch" spacing="10px" w="100%" pt="8px">
              {isStrikePaused && (
                <Box
                  border="1px solid"
                  borderColor="red.200"
                  bg={strikePausedBg}
                  borderRadius="16px"
                  p="14px"
                >
                  <Badge colorScheme="red" borderRadius="full" mb="10px">
                    Account review available
                  </Badge>
                  <Text color={textColor} fontWeight="800">
                    Class opportunities are paused
                  </Text>
                  <Text color={mutedText} fontSize="sm" mt="6px">
                    Your account is paused after recent late cancellations. Send the team a review request if there is context we should know.
                  </Text>
                  {isStrikeAppealSubmitted ? (
                    <Alert status="success" borderRadius="12px" mt="12px">
                      <AlertIcon />
                      Review request submitted
                    </Alert>
                  ) : (
                    <>
                      <Textarea
                        value={strikeAppealMessage}
                        onChange={(event) => setStrikeAppealMessage(event.target.value)}
                        placeholder="Tell us what happened"
                        mt="12px"
                        minH="110px"
                        {...profileInputStyles}
                      />
                      <Button
                        colorScheme="brand"
                        mt="12px"
                        w="100%"
                        onClick={handleSubmitStrikeAppeal}
                        isLoading={isSubmittingStrikeAppeal}
                        isDisabled={!strikeAppealMessage.trim()}
                      >
                        Request review
                      </Button>
                    </>
                  )}
                </Box>
              )}
              <Box
                border="1px solid"
                borderColor={borderColor}
                bg={softBg}
                borderRadius="16px"
                p="14px"
              >
                <Flex align="flex-start" justify="space-between" gap="14px">
                  <Box>
                    <Text color={textColor} fontWeight="800">
                      Pause class opportunity outreach
                    </Text>
                    <Text color={mutedText} fontSize="sm" mt="6px">
                      {canManageCommunicationPreference
                        ? 'Turn this off to pause class opportunity outreach by email or text.'
                        : 'This setting cannot be changed here. Please reach out to techsupport@fitgroupusa.com for help.'}
                    </Text>
                  </Box>
                  <Switch
                    colorScheme="blue"
                    isChecked={receivesOpportunityOutreach}
                    isDisabled={!canManageCommunicationPreference || isUpdatingCommunicationPreference}
                    onChange={handleCommunicationPreferenceChange}
                    aria-label="Receive class opportunity outreach"
                  />
                </Flex>
                {!canManageCommunicationPreference && (
                  <Alert status="warning" borderRadius="12px" mt="12px">
                    <AlertIcon />
                    <Text fontSize="sm">
                      Contact techsupport@fitgroupusa.com to update communication preferences.
                    </Text>
                  </Alert>
                )}
              </Box>
              {profileBadges.map((badge) => (
                <Box
                  key={badge.title}
                  border="1px solid"
                  borderColor={borderColor}
                  bg={softBg}
                  borderRadius="16px"
                  p="14px"
                >
                  <HStack align="flex-start" spacing="12px">
                    <Flex
                      align="center"
                      justify="center"
                      boxSize="36px"
                      borderRadius="full"
                      bg={`${badge.colorScheme}.100`}
                      color={`${badge.colorScheme}.600`}
                      flexShrink={0}
                    >
                      <Icon as={FaMedal} />
                    </Flex>
                    <Box>
                      <Badge colorScheme={badge.colorScheme}>{badge.title}</Badge>
                      <Text color={mutedText} fontSize="sm" mt="6px">
                        {badge.description}
                      </Text>
                    </Box>
                  </HStack>
                </Box>
              ))}
            </VStack>
          </Flex>
        </Card>

        <Card p="24px" gridColumn={{ base: 'auto', xl: 'span 2' }}>
          <SimpleGrid columns={{ base: 1, md: 2 }} gap="18px">
            <FormControl>
              <FormLabel>First Name</FormLabel>
              <Input isDisabled value={form.FirstName} {...profileInputStyles} />
            </FormControl>
            <FormControl>
              <FormLabel>Last Name</FormLabel>
              <Input isDisabled value={form.LastName} {...profileInputStyles} />
            </FormControl>
            <FormControl>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                value={form.Email}
                onChange={(event) => updateField('Email', event.target.value)}
                {...profileInputStyles}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Phone</FormLabel>
              <Input
                value={form.Phone}
                onChange={(event) => updateField('Phone', event.target.value)}
                {...profileInputStyles}
              />
            </FormControl>

            <FormControl gridColumn={{ base: 'auto', md: 'span 2' }}>
              <FormLabel>Address</FormLabel>
              <GoogleMapsLoader
                loadingElement={(
                  <Input
                    value={form.Address}
                    onChange={(event) => updateField('Address', event.target.value)}
                    placeholder="Start typing your address"
                    {...profileInputStyles}
                  />
                )}
              >
                <Autocomplete
                  options={US_ADDRESS_AUTOCOMPLETE_OPTIONS}
                  onLoad={(autocomplete) => {
                    autocompleteRef.current = autocomplete;
                  }}
                  onPlaceChanged={handlePlaceChanged}
                >
                  <Input
                    value={form.Address}
                    onChange={(event) => updateField('Address', event.target.value)}
                    placeholder="Start typing your address"
                    {...profileInputStyles}
                  />
                </Autocomplete>
              </GoogleMapsLoader>
            </FormControl>
            <FormControl>
              <FormLabel>City</FormLabel>
              <Input
                value={form.City}
                onChange={(event) => updateField('City', event.target.value)}
                {...profileInputStyles}
              />
            </FormControl>
            <FormControl>
              <FormLabel>State</FormLabel>
              <Input
                value={form.State}
                onChange={(event) => updateField('State', event.target.value)}
                {...profileInputStyles}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Zip</FormLabel>
              <Input
                value={form.Zip}
                onChange={(event) => updateField('Zip', event.target.value)}
                {...profileInputStyles}
              />
            </FormControl>

            <FormControl gridColumn={{ base: 'auto', md: 'span 2' }}>
              <FormLabel>Travel Distance</FormLabel>
              <SimpleGrid columns={{ base: 1, md: 3 }} gap="12px">
                {TRAVEL_DISTANCE_OPTIONS.map((option) => {
                  const isSelected = form.distanceValue === option.value;

                  return (
                    <Button
                      key={option.value}
                      type="button"
                      h="auto"
                      minH="94px"
                      p="14px"
                      whiteSpace="normal"
                      alignItems="flex-start"
                      justifyContent="flex-start"
                      textAlign="left"
                      variant="outline"
                      borderWidth="2px"
                      borderColor={isSelected ? selectedTravelBorder : borderColor}
                      bg={isSelected ? selectedTravelBg : 'transparent'}
                      onClick={() => updateField('distanceValue', option.value)}
                    >
                      <Box>
                        <Text color={textColor} fontSize="md" fontWeight="800">
                          {option.title}
                        </Text>
                        <Text color={mutedText} fontSize="sm" mt="5px">
                          {option.description}
                        </Text>
                      </Box>
                    </Button>
                  );
                })}
              </SimpleGrid>
            </FormControl>

            <FormControl gridColumn={{ base: 'auto', md: 'span 2' }}>
              <FormLabel>Class Types</FormLabel>
              <HStack align="flex-start" spacing="10px">
                <Select
                  placeholder="Choose a class type"
                  value={selectedClassType}
                  onChange={(event) => setSelectedClassType(event.target.value)}
                  {...profileSelectStyles}
                >
                  {availableClassTypes.map((classType) => (
                    <option key={classType} value={classType}>
                      {classType}
                    </option>
                  ))}
                </Select>
                <Button colorScheme="brand" onClick={addClassType} isDisabled={!selectedClassType}>
                  Add
                </Button>
              </HStack>
              <Box mt="12px" p="14px" border="1px solid" borderColor={borderColor} borderRadius="14px" bg={softBg}>
                <FormLabel mb="8px">
                  Other class type
                  <Badge ml="8px" colorScheme="purple" borderRadius="full">
                    User entered
                  </Badge>
                </FormLabel>
                <HStack align="flex-start" spacing="10px">
                  <Input
                    value={customClassTypeInput}
                    onChange={(event) => setCustomClassTypeInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        addCustomClassType();
                      }
                    }}
                    placeholder="Type a class type"
                    {...profileInputStyles}
                  />
                  <Button colorScheme="brand" onClick={addCustomClassType} isDisabled={!customClassTypeInput.trim()}>
                    Add
                  </Button>
                </HStack>
              </Box>
              <Flex gap="10px" flexWrap="wrap" mt="12px">
                {form.ClassTypes.map((classType) => {
                  const icon = getClassTypeIcon(classType);

                  return (
                    <Tag key={classType} size="lg" borderRadius="full" colorScheme="blue" py="6px">
                      {icon ? (
                        <Image src={icon} alt="" boxSize="22px" objectFit="contain" mr="8px" />
                      ) : null}
                      <TagLabel>{classType}</TagLabel>
                      <TagCloseButton onClick={() => removeClassType(classType)} />
                    </Tag>
                  );
                })}
                {normalizeInstructorCustomClassTypes(form.CustomClassTypes).map((classType) => (
                  <Tag key={classType.value} size="lg" borderRadius="full" colorScheme="purple" py="6px">
                    <TagLabel>{classType.value}</TagLabel>
                    <Badge ml="8px" colorScheme="purple" borderRadius="full">
                      User entered
                    </Badge>
                    <TagCloseButton onClick={() => removeCustomClassType(classType.value)} />
                  </Tag>
                ))}
              </Flex>
            </FormControl>

                <FormControl>
                  <FormLabel>Agreement Date</FormLabel>
                  <Input
                    isDisabled
                    value={userData?.instructorAgreementDate
                      ? new Date(userData.instructorAgreementDate).toLocaleDateString()
                      : 'Not signed'}
                    {...profileInputStyles}
                  />
                </FormControl>
              </SimpleGrid>

          <Flex
            align={{ base: 'stretch', md: 'center' }}
            justify="space-between"
            gap="14px"
            mt="24px"
            direction={{ base: 'column', md: 'row' }}
          >
            <HStack color={mutedText} spacing="8px">
              <Icon as={FaMapMarkerAlt} />
              <Text fontSize="sm">
                Your address helps match nearby opportunities.
              </Text>
            </HStack>
            <Button colorScheme="brand" onClick={handleSave} isLoading={isSaving}>
              Save Profile
            </Button>
          </Flex>
        </Card>
      </SimpleGrid>
      <AlertDialog
        isOpen={pauseOutreachDisclosure.isOpen}
        leastDestructiveRef={cancelPauseRef}
        onClose={pauseOutreachDisclosure.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="20px">
            <AlertDialogHeader fontWeight="900">
              Pause class opportunity outreach?
            </AlertDialogHeader>
            <AlertDialogBody color={mutedText}>
              This will pause class opportunity outreach by email and text. You can turn it back on from your profile when you are ready.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelPauseRef} onClick={pauseOutreachDisclosure.onClose}>
                Keep outreach on
              </Button>
              <Button
                colorScheme="orange"
                ml={3}
                onClick={confirmPauseCommunicationPreference}
                isLoading={isUpdatingCommunicationPreference}
              >
                Pause outreach
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
